import { 
	Typography, 
	Box,
	LinearProgress, 
	Avatar, 
	useMediaQuery, 
	Theme,
	Container, 
	alpha, 
	useTheme,
	Fab,
	Divider,
	Button
} from '@mui/material';
import { Dialog } from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { PlanImageItem, QuickOrderProduct } from '../types';
import { useFindWithCustomPropertiesQuery } from '../services/products';
import { DialogHeader } from './DialogHeader';
import { useTranslation } from 'react-i18next';
import placeholderImage from '../assets/product-placeholder.png';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { QuickOrderCardAdjustQuantitySection } from '../containers/plans/components/QuickOrderCard';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';

interface ProductDetailDialogFullScreenProps {
	open: boolean;
	onClose: () => void;
	product: QuickOrderProduct | null;
	onQuantityChange: (productId: number, newQuantity: number) => void;
	selectedQuantity: string;
	maxQuantity?: number;
	disabled?: boolean;
}

export const ProductDetailDialogFullScreen = ({ open, onClose, product, onQuantityChange, selectedQuantity, maxQuantity, disabled }: ProductDetailDialogFullScreenProps) => {
	const [cachedProduct, setCachedProduct] = useState<QuickOrderProduct | null>(null);
	const [currentPanel, setCurrentPanel] = useState(0);
	const { t } = useTranslation();
	const isMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
	const theme = useTheme();
	const { data: productData, isFetching: isFetchingProductData } = useFindWithCustomPropertiesQuery(product?.productId ?? 0, { skip: !product?.productId });
	const {
		customProperties = [],
		sku,
		description
	} = productData ?? {};

	const images: PlanImageItem[] = productData?.images ?? [];
	const totalPanels = isMdUp && images.length > 0 ? images.length : 1 + images.length; // On md+ screens, first image is merged with product info

	useEffect(() => {
		if (product) {
			setCachedProduct(product);
			setCurrentPanel(0); // Reset to first panel when product changes
		}
	}, [product]);

	const handleKeyDown = useCallback((event: KeyboardEvent) => {
		if (!open) return;
		
		switch (event.key) {
		case 'ArrowLeft':
			event.preventDefault();
			setCurrentPanel(prev => Math.max(0, prev - 1));
			break;
		case 'ArrowRight':
			event.preventDefault();
			setCurrentPanel(prev => Math.min(totalPanels - 1, prev + 1));
			break;
		case 'Escape':
			event.preventDefault();
			onClose();
			break;
		}
	}, [open, totalPanels, onClose]);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleKeyDown]);

	const goToPrevious = () => {
		setCurrentPanel(prev => Math.max(0, prev - 1));
	};

	const goToNext = () => {
		setCurrentPanel(prev => Math.min(totalPanels - 1, prev + 1));
	};

	const renderProductInfoPanel = () => (
		<Box sx={{ 
			width: '100%', 
			height: '100%', 
			display: 'flex', 
			flexDirection: 'column',
		}}>
			<Box display='flex' flexDirection='column' flex={1} sx={{overflowY: 'auto', pb: 3, pt: 2}}>
				<Container>
					{!isMdUp && cachedProduct?.imgUrl && <img src={cachedProduct?.imgUrl} alt={cachedProduct?.name} style={{width: '100%', objectFit: 'contain', maxHeight: '400px', backgroundColor: theme.palette.grey[200]}} />}
					<Box gap={2} display='flex' flexDirection='column'>
						<Box>
							<Typography variant='body2' color="text.secondary">{t('productView.sku')}</Typography>
							<Typography variant='body1' sx={{whiteSpace: 'pre-line'}}>{sku}</Typography>
						</Box>
						{customProperties.map((property: any) => (
							<Box key={property.id}>
								<Typography variant='body2' color="text.secondary">{property.name}</Typography>
								<Typography variant='body1' sx={{whiteSpace: 'pre-line'}}>{property.value}</Typography>
							</Box>
						))}
						{description && <Box>
							<Typography variant='body2' color="text.secondary">{t('productView.description')}</Typography>
							<Typography variant='body1' sx={{whiteSpace: 'pre-line'}}>{description}</Typography>
						</Box>}
					</Box>
				</Container>
			</Box>
			{Boolean(cachedProduct?.documentsCount) && (
				<Container sx={{ 
					backgroundColor: alpha(theme.palette.text.primary, 0.05), 
					p: 2, 
					boxShadow: '0 -10px 10px 0 rgba(0,0,0,0.15)'
				}}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<CloudDownloadIcon sx={{ fontSize: 16, color: 'success.main' }} />
						<Typography variant='body2' color='success.main'>{t('productView.includesDigitalDelivery')}</Typography>
					</Box>
					<Typography variant='body2' sx={{whiteSpace: 'pre-line'}}>{t('productView.includesDigitalDeliveryDescription')}</Typography>
				</Container>
			)}
			{product && <Box display='flex' justifyContent='center' sx={{mt: 2, mb: 2, height: '40px'}}>
				{Number(selectedQuantity) > 0 ? 
					<QuickOrderCardAdjustQuantitySection quantity={Number(selectedQuantity)} uom={product.uom as string} onQuantityChange={newQuantity => onQuantityChange?.(product.id as number, newQuantity)} isAddDisabled={disabled || Boolean(maxQuantity && Number(selectedQuantity) >= maxQuantity)} />
					: <Button
						variant="contained"
						onClick={() => onQuantityChange?.(product.id as number, 1)}
						disabled={disabled}
						startIcon={<AddShoppingCartIcon />}
					>
						{t('planViewQuickOrderCard.addToCart')}
					</Button>}
			</Box>}
		</Box>
	);

	const renderCombinedPanel = () => (
		<Box sx={{ 
			width: '100vw', 
			height: '100%', 
			display: 'flex', 
			flexDirection: 'row',
			flexShrink: 0
		}}>
			{/* Product info - 1/3 width */}
			<Box sx={{ 
				width: '33.333%',
				minWidth: '400px',
				height: '100%', 
				display: 'flex', 
				flexDirection: 'column',
				borderRight: '1px solid',
				borderColor: 'divider'
			}}>
				<Box gap={2} display='flex' flexDirection='column' flex={1} sx={{overflowY: 'auto', pb: 3, pt: 2}}>
					<Container>
						{!isMdUp && <Avatar src={cachedProduct?.imgUrl} variant='square' sx={{width: 150, height: 150}} >
							<img src={placeholderImage} alt={cachedProduct?.name} width={150} height={150} />
						</Avatar>}
						<Box display='flex' flexDirection='column' gap={2}>
							<Box>
								<Typography variant='body2' color="text.secondary">{t('productView.sku')}</Typography>
								<Typography variant='body1' sx={{whiteSpace: 'pre-line'}}>{sku}</Typography>
							</Box>
							{customProperties.map((property: any) => (
								<Box key={property.id}>
									<Typography variant='body2' color="text.secondary">{property.name}</Typography>
									<Typography variant='body1' sx={{whiteSpace: 'pre-line'}}>{property.value}</Typography>
								</Box>
							))}
							{description && <Box>
								<Typography variant='body2' color="text.secondary">{t('productView.description')}</Typography>
								<Typography variant='body1' sx={{whiteSpace: 'pre-line'}}>{description}</Typography>
							</Box>}
						</Box>
					</Container>
				</Box>
				{Boolean(cachedProduct?.documentsCount) && (
					<Container sx={{ 
						backgroundColor: alpha(theme.palette.text.primary, 0.05), 
						p: 2, 
						boxShadow: '0 -10px 10px 0 rgba(0,0,0,0.15)',
					}}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<CloudDownloadIcon sx={{ fontSize: 16, color: 'success.main' }} />
							<Typography variant='body2' color='success.main'>{t('productView.includesDigitalDelivery')}</Typography>
						</Box>
						<Typography variant='body2' sx={{whiteSpace: 'pre-line'}}>{t('productView.includesDigitalDeliveryDescription')}</Typography>
					</Container>
				)}
				<Box display='flex' justifyContent='center' sx={{mt: 2, mb: 2, height: '40px'}}>
					{Number(selectedQuantity) > 0 ? 
						<QuickOrderCardAdjustQuantitySection quantity={Number(selectedQuantity)} uom={cachedProduct?.uom as string} onQuantityChange={newQuantity => onQuantityChange?.(cachedProduct?.id as number, newQuantity)} isAddDisabled={disabled || Boolean(maxQuantity && Number(selectedQuantity) >= maxQuantity)} sx={{mt: 2, mb: 2}} />
						: <Button
							variant="contained"
							onClick={() => onQuantityChange?.(cachedProduct?.id as number, 1)}
							disabled={disabled}
							startIcon={<AddShoppingCartIcon />}
						>
							{t('planViewQuickOrderCard.addToCart')}
						</Button>}
				</Box>
			</Box>
			
			{/* First image - 2/3 width */}
			<Box sx={{ 
				width: '66.667%', 
				height: '100%', 
				display: 'flex', 
				alignItems: 'center', 
				justifyContent: 'center',
				position: 'relative',
				backgroundColor: 'black'
			}}>
				<img 
					src={cachedProduct?.imgUrl || placeholderImage} 
					alt={'product image 1 of ' + cachedProduct?.name}
					style={{
						maxWidth: '100%',
						maxHeight: '100%',
						objectFit: 'contain'
					}}
				/>
			</Box>
		</Box>
	);

	const renderImagePanel = (image: PlanImageItem, index: number) => (
		<Box sx={{ 
			width: '100%', 
			height: '100%', 
			display: 'flex', 
			alignItems: 'center', 
			justifyContent: 'center',
			position: 'relative',
			backgroundColor: 'black'
		}}>
			<img 
				src={image.imgUrl || placeholderImage} 
				alt={`product image ${index + 1} of ${cachedProduct?.name}`}
				style={{
					maxWidth: '100%',
					maxHeight: '100%',
					objectFit: 'contain'
				}}
			/>
			{image.title && (
				<Box sx={{
					position: 'absolute',
					bottom: 20,
					left: 20,
					right: 20,
					backgroundColor: 'rgba(0,0,0,0.7)',
					color: 'white',
					p: 2,
					borderRadius: 1
				}}>
					<Typography variant='body1'>{image.title}</Typography>
				</Box>
			)}
		</Box>
	);

	return (
		<Dialog open={open} onClose={onClose} fullScreen>
			<DialogHeader title={cachedProduct?.name ?? ''} onClose={onClose} />
			<Divider />
			{isFetchingProductData ? (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
					<LinearProgress sx={{ width: '50%' }} />
				</Box>
			) : (
				<Box sx={{ 
					position: 'relative', 
					height: '100%', 
					overflow: 'hidden'
				}}>
					{/* Main content area */}
					<Box sx={{ 
						display: 'flex', 
						width: `${totalPanels * 100}vw`, 
						height: '100%',
						transform: `translateX(-${currentPanel * 100}vw)`,
						transition: 'transform 0.3s ease-in-out'
					}}>
						{/* On md+ screens with images, show combined panel first */}
						{isMdUp && (images.length > 0 || cachedProduct?.imgUrl) ? (
							<>
								{/* Combined product info + first image panel */}
								<Box sx={{ width: '100vw', height: '100%', flexShrink: 0 }}>
									{renderCombinedPanel()}
								</Box>
								
								{/* Remaining image panels */}
								{images.slice(1).map((image, index) => (
									<Box key={image.id || index + 1} sx={{ width: '100vw', height: '100%', flexShrink: 0 }}>
										{renderImagePanel(image, index + 1)}
									</Box>
								))}
							</>
						) : (
							<>
								{/* Product info panel */}
								<Box sx={{ width: '100vw', height: '100%', flexShrink: 0 }}>
									{renderProductInfoPanel()}
								</Box>
								
								{/* Image panels */}
								{images.map((image, index) => (
									<Box key={image.id || index} sx={{ width: '100vw', height: '100%', flexShrink: 0 }}>
										{renderImagePanel(image, index)}
									</Box>
								))}
							</>
						)}
					</Box>

					{/* Navigation buttons */}
					{currentPanel > 0 && (
						<Fab
							size="small"
							onClick={goToPrevious}
							sx={{
								position: 'absolute',
								left: 16,
								top: '50%',
								transform: 'translateY(-50%)',
								backgroundColor: 'rgba(255,255,255,0.9)',
								'&:hover': {
									backgroundColor: 'rgba(255,255,255,1)'
								}
							}}
						>
							<ChevronLeftIcon />
						</Fab>
					)}

					{currentPanel < totalPanels - 1 && (
						<Fab
							size="small"
							onClick={goToNext}
							sx={{
								position: 'absolute',
								right: 16,
								top: '50%',
								transform: 'translateY(-50%)',
								backgroundColor: 'rgba(255,255,255,0.9)',
								'&:hover': {
									backgroundColor: 'rgba(255,255,255,1)'
								}
							}}
						>
							<ChevronRightIcon />
						</Fab>
					)}

					{/* Panel indicator */}
					{totalPanels > 1 && (
						<Box sx={{
							position: 'absolute',
							bottom: 32,
							left: isMdUp ? '50%' : undefined,
							right: isMdUp ? undefined : 16,
							transform: 'translateX(-50%)',
							display: 'flex',
							gap: 1
						}}>
							{Array.from({ length: totalPanels }, (_, index) => (
								<Box
									key={index}
									sx={{
										width: 8,
										height: 8,
										borderRadius: '50%',
										backgroundColor: currentPanel === index ? 'white' : 'rgba(255,255,255,0.5)',
										cursor: 'pointer',
										transition: 'background-color 0.2s'
									}}
									onClick={() => setCurrentPanel(index)}
								/>
							))}
						</Box>
					)}
				</Box>
			)}
		</Dialog>
	);
};
